/**
 * The workflow definition: the body of an extension of kind `ExtensionKind.Workflow`, types only.
 * It is data, not code: an ordered list of steps the `entity.workflow` runtime cell interprets, and the format the editor saves and the checked-in fixtures use.
 * Every step carries a stable `id`; the runtime derives each action's durable checkpoint and operation key from it (plus the loop iteration), so renaming an id in a published revision makes it a different action.
 * `validateWorkflowDefinition` (`workflow.definition.validation.ts`) is the authority on what a valid definition is; the runtime cell mirrors these types.
 */
import type { Entity } from '../entity';
import type { JsonSafeMetadata } from '../surface.engine/json.safe.metadata.zod';

/** The only schema version; a definition of another version is refused rather than guessed at. */
export const WORKFLOW_SCHEMA_VERSION = 1;

/** One workflow: what runs, in order, when its extension's trigger fires. */
export interface WorkflowDefinition {
    schemaVersion: typeof WORKFLOW_SCHEMA_VERSION;
    steps: WorkflowStep[];
    /** Values the run keeps as it walks, set by `setVariable` steps and read as `variable` values; absent means none. */
    variables?: WorkflowVariable[];
    /** What a newer event on a Job does while this workflow's run on that Job is waiting; absent means `Queue`. */
    whenRunning?: WorkflowWhenRunning;
    /** Canvas positions keyed by step id, for the editor only; execution never reads it. */
    layout?: Record<string, WorkflowStepPosition>;
}

/**
 * What a newer event on a Job does while this workflow's run on that Job is Waiting. Either way the workflow's events on a Job
 * never run side by side: `Queue` holds the newer event until the waiting run finishes; `Replace` cancels the waiting run so the
 * newer event runs now.
 */
export enum WorkflowWhenRunning {
    Queue = 'queue',
    Replace = 'replace',
}

/**
 * One declared workflow variable. It lives in the run's walk, not in a checkpoint: a replay recomputes it from the same steps, so
 * it holds only what the walk itself derives.
 */
export interface WorkflowVariable {
    /** Unique within the definition; `^[a-z][A-Za-z0-9_]{0,63}$`, so text reads it as the merge field `vars.<name>`. */
    name: string;
    /** Its type: `MetadataEntityFieldType.String`, `Number` or `Boolean` (`WORKFLOW_VARIABLE_TYPES`). */
    metadata: JsonSafeMetadata;
    /** The value before any step sets it; absent reads as `null`. */
    initial?: WorkflowLiteral;
}

/** Where the editor draws one step. */
export interface WorkflowStepPosition {
    x: number;
    y: number;
}

/** One node of the step tree. */
export type WorkflowStep = WorkflowActionStep | WorkflowIfStep | WorkflowSwitchStep | WorkflowForEachStep | WorkflowStopStep | WorkflowWaitStep | WorkflowSetVariableStep;

/** The step kinds. */
export enum WorkflowStepType {
    Action = 'action',
    If = 'if',
    Switch = 'switch',
    ForEach = 'forEach',
    Stop = 'stop',
    Wait = 'wait',
    SetVariable = 'setVariable',
}

/** What every step carries. */
interface WorkflowStepBase {
    /** Unique within the definition; `^[a-z][A-Za-z0-9_-]{0,63}$`. */
    id: string;
    /** The label the editor and run history show. */
    name?: string;
}

/** Performs one WorkBuddy action through the public API; its output is visible to later steps as `steps.<id>`. */
export interface WorkflowActionStep extends WorkflowStepBase {
    type: WorkflowStepType.Action;
    action: WorkflowActionType;
    /** Values for the action's inputs, keyed by the names its `WORKFLOW_ELEMENTS` fields bind as `input.<name>`. */
    input: Record<string, WorkflowValue>;
}

/** Runs `then` when `condition` holds, otherwise `else`. */
export interface WorkflowIfStep extends WorkflowStepBase {
    type: WorkflowStepType.If;
    condition: WorkflowCondition;
    then: WorkflowStep[];
    else: WorkflowStep[];
}

/** Runs the first case whose `equals` matches `value`, otherwise `default`. */
export interface WorkflowSwitchStep extends WorkflowStepBase {
    type: WorkflowStepType.Switch;
    value: WorkflowValue;
    cases: WorkflowSwitchCase[];
    default: WorkflowStep[];
}

/** One branch of a switch. */
export interface WorkflowSwitchCase {
    /** Unique within the definition, like a step id; run history reports which case ran. */
    id: string;
    /** Optional human label shown on the canvas branch header. */
    label?: string;
    equals: WorkflowLiteral | WorkflowLiteral[];
    steps: WorkflowStep[];
}

/** Runs `steps` once per item of `items`, in order; more items than `maxItems` fails the run instead of truncating silently. */
export interface WorkflowForEachStep extends WorkflowStepBase {
    type: WorkflowStepType.ForEach;
    items: WorkflowValue;
    /** 1 to `WORKFLOW_LIMITS.maxLoopItems`. */
    maxItems: number;
    steps: WorkflowStep[];
}

/** Ends the run as completed; steps after it never run. */
export interface WorkflowStopStep extends WorkflowStepBase {
    type: WorkflowStepType.Stop;
    /** Merge text read back as plain text; absent stops without a reason. */
    reason?: WorkflowTextValue;
}

/**
 * Pauses the run for `duration`, then continues with the next step against the Job as it is then. Valid only in a workflow:
 * while it waits the run frees its Job's lane, and it rejoins the lane behind whatever queued meanwhile.
 */
export interface WorkflowWaitStep extends WorkflowStepBase {
    type: WorkflowStepType.Wait;
    /** `<count> <unit>`, unit one of minute(s), hour(s), day(s); between 1 minute and `WORKFLOW_LIMITS.maxWaitDays` days. */
    duration: string;
}

/** What a `setVariable` step does with its value. */
export enum WorkflowVariableOp {
    /** Replaces the variable's value. */
    Set = 'set',
    /** Adds to a number variable; an unset variable counts as 0. */
    Add = 'add',
}

/** Sets or adds to a declared variable; later steps on the same walk, loop iterations included, read the new value. */
export interface WorkflowSetVariableStep extends WorkflowStepBase {
    type: WorkflowStepType.SetVariable;
    /** The `name` of a declared variable. */
    variable: string;
    op: WorkflowVariableOp;
    value: WorkflowValue;
}

/**
 * A record picked in the editor, saved small the way a Job saves its priority: `_id` is what the run compares and what an action
 * sends; `name`, `color` and a stage status's `systemStatus` let the editor show it without a lookup, and `name` is what merge
 * text prints. It equals a run value that is that id or any record with that `_id`.
 */
export type WorkflowRecordLiteral = Pick<Entity, '_id' | 'name' | 'color'> & { systemStatus?: string };

/** A plain JSON scalar, or a picked record (`WorkflowRecordLiteral`). */
export type WorkflowLiteral = string | number | boolean | null | WorkflowRecordLiteral;

/**
 * Where a step input or comparison operand comes from. There is no expression language: only literals, paths into data the run already has, lists of values, and composed merge-HTML text.
 * A path that does not resolve reads as `null`; it never throws.
 */
export type WorkflowValue = WorkflowLiteralValue | WorkflowTriggerValue | WorkflowStepOutputValue | WorkflowLoopItemValue | WorkflowTextValue | WorkflowSettingValue | WorkflowFlagValue | WorkflowVariableValue | WorkflowListValue | WorkflowNowValue;

/** The value sources. */
export enum WorkflowValueSource {
    Literal = 'literal',
    Trigger = 'trigger',
    Step = 'step',
    Item = 'item',
    Text = 'text',
    Setting = 'setting',
    Flag = 'flag',
    Variable = 'variable',
    List = 'list',
    Now = 'now',
}

/** Display preset for formatting a date, time, or number token into text. */
export type WorkflowTokenFormat = 'date' | 'dateLong' | 'dateNumeric' | 'dateTime' | 'time' | 'number' | 'currency';

/** All supported `WorkflowTokenFormat` values, in picker display order. */
export const WORKFLOW_TOKEN_FORMATS: readonly WorkflowTokenFormat[] = ['date', 'dateNumeric', 'dateLong', 'dateTime', 'time', 'number', 'currency'];

/** Optional display formatting applied when a token is rendered into text or a formatted field. */
export interface WorkflowValueFormatOptions {
    format?: WorkflowTokenFormat;
    /** Decimal places (`0`..`6`) when `format` is `'number'` or `'currency'`. */
    decimals?: number;
    /** Fallback text rendered when the source resolves to `null`, `undefined`, `''`, or `[]`. */
    ifEmpty?: string;
    /** Separator used when a list value is rendered as text; defaults to `', '`. */
    join?: string;
}

export interface WorkflowLiteralValue {
    source: WorkflowValueSource.Literal;
    /** A list only where one is taken: the right of `in` and `notIn`, or an action input whose field is a list; the validator refuses one anywhere else. */
    value: WorkflowLiteral | WorkflowLiteral[];
}

/** An ordered list of scalar literals and/or data bindings (`labels`, `to` recipients, `in`/`notIn` operands). */
export interface WorkflowListValue {
    source: WorkflowValueSource.List;
    items: WorkflowValue[];
}

/** The run's current timestamp (evaluated in the Job's/tenant's time zone for date comparisons and formatting). */
export interface WorkflowNowValue extends WorkflowValueFormatOptions {
    source: WorkflowValueSource.Now;
}

/** A dotted path into the run's trigger data: `job.<field>` (the run's Job view), `trigger.<field>` (events, changedFields), `workflow.name`, `run.startedAt`, or `now`. */
export interface WorkflowTriggerValue extends WorkflowValueFormatOptions {
    source: WorkflowValueSource.Trigger;
    path: string;
}

/** A dotted path into an earlier action's output; the action must run before this step on every path that reaches it. */
export interface WorkflowStepOutputValue extends WorkflowValueFormatOptions {
    source: WorkflowValueSource.Step;
    stepId: string;
    /** Empty or absent for the whole output. */
    path?: string;
}

/** The current item of the nearest enclosing forEach, or a dotted path into it. */
export interface WorkflowLoopItemValue extends WorkflowValueFormatOptions {
    source: WorkflowValueSource.Item;
    path?: string;
}

/**
 * Composed text: merge HTML as WorkBuddy's shared Froala merge editors write it (see `workflow.merge.html.ts`), so workflows and
 * merge templates share one merge system. Record tags keep their native merge names and render on the server; workflow tags
 * (`steps.`, `item`, `vars.`, `settings.`, `flags.`, `trigger.`, `workflow.name`, `run.startedAt`, `now`) render in the run.
 */
export interface WorkflowTextValue {
    source: WorkflowValueSource.Text;
    /** Merge HTML: text with `<span data-wb-merge="<name>">` fields; at most `WORKFLOW_LIMITS.maxTextLength` characters. */
    html: string;
}

/** How a merge span reads, as the `#` picker writes it: `[label]`, titled `label from source`. */
export interface WorkflowMergeTagLabel {
    /** The picker's display name, such as `Job Number` or `Stage`. */
    label: string;
    /** The picker group it came from, such as `Job` or `This stage`; absent leaves the span untitled. */
    source?: string;
}

/** One merge field of workflow merge HTML, as `workflowMergeTags` reads it; its format options come from `data-wb-*` attributes. */
export interface WorkflowMergeTag extends WorkflowValueFormatOptions {
    /** The `data-wb-merge` name, such as `steps.note.activityId` or `job.jobNumber`. */
    name: string;
    /** What a workflow tag reads; null for a record tag, which the server's merge renderer resolves against the run's record. */
    value: WorkflowValue | null;
    /** Where the whole `<span>…</span>` starts in the HTML. */
    start: number;
    /** Where the whole `<span>…</span>` ends (exclusive). */
    end: number;
}

/** A tenant setting the run may read (`ExtensionSettingKey`), or a dotted path into an object setting; read once per run. */
export interface WorkflowSettingValue extends WorkflowValueFormatOptions {
    source: WorkflowValueSource.Setting;
    key: string;
    /** Empty or absent for the whole value. */
    path?: string;
}

/** Whether a feature flag (`FeatureFlags` value) is on for the tenant; read once per run, so the whole run sees one answer. */
export interface WorkflowFlagValue extends WorkflowValueFormatOptions {
    source: WorkflowValueSource.Flag;
    flag: string;
}

/** The current value of a declared variable. */
export interface WorkflowVariableValue extends WorkflowValueFormatOptions {
    source: WorkflowValueSource.Variable;
    name: string;
}

/** A comparison, or an AND/OR/NOT over conditions. */
export type WorkflowCondition = WorkflowAllCondition | WorkflowAnyCondition | WorkflowNotCondition | WorkflowComparison;

export interface WorkflowAllCondition {
    all: WorkflowCondition[];
}

export interface WorkflowAnyCondition {
    any: WorkflowCondition[];
}

export interface WorkflowNotCondition {
    not: WorkflowCondition;
}

/**
 * `left operator right`; `isSet` and `isNotSet` take no `right`.
 * `equals` and `notEquals` compare text exactly (case matters) and records by id. `contains` and `notContains` match a list item
 * exactly (by id when comparing records) or text as a case-insensitive substring. `in` and `notIn` take a list on the right (`list`
 * value, literal values, or a binding to a list): `in` holds when the left value, or any item of a list on the left, equals one of them.
 * `isOn`, `before` and `after` take two dates and compare their calendar days in the tenant's time zone, so a time of day never
 * changes the answer. `greaterThan` and `lessThan` compare numbers, or two ISO dates as exact instants.
 */
export interface WorkflowComparison {
    left: WorkflowValue;
    operator: WorkflowOperator;
    right?: WorkflowValue;
}

export enum WorkflowOperator {
    Equals = 'equals',
    NotEquals = 'notEquals',
    IsSet = 'isSet',
    IsNotSet = 'isNotSet',
    Contains = 'contains',
    NotContains = 'notContains',
    GreaterThan = 'greaterThan',
    LessThan = 'lessThan',
    In = 'in',
    NotIn = 'notIn',
    IsOn = 'isOn',
    /** The left date's calendar day is earlier than the right's. */
    Before = 'before',
    /** The left date's calendar day is later than the right's. */
    After = 'after',
}

/** The WorkBuddy actions a workflow can perform; each maps to one public API endpoint on the run's Job. */
export enum WorkflowActionType {
    /** `POST /api/v2/public/jobs/:jobId/activities/note`; output `{ activityId }`. */
    AddJobNote = 'job.note.add',
    /** `POST /api/v2/public/jobs/:jobId/activities/email` with a template; the template fills subject, body and recipients the step leaves out. Output `{ activityId }`. */
    SendJobEmail = 'job.email.send',
    /** `POST /api/v2/public/jobs/:jobId/activities/sms` with a template, sent once per run and step (`Idempotency-Key`); the template fills the message and recipients the step leaves out, `to` being comma or semicolon separated mobile numbers. Output `{ activityId }`. */
    SendJobSms = 'job.sms.send',
    /** `GET /api/v2/public/jobs/:jobId/stages`; output `{ items: [{ id, name, stageNumber, status, statusName }] }`, `status` being the public system status (`open`, `completed`, ...), so a loop can go over `steps.<id>.items`. */
    ListJobStages = 'job.stages.list',
    /** `POST /api/v2/public/stages/:stageId/complete` for a stage of the run's Job; a stage already completed, finalised or closed is left as it is. Output `{ stageId, status, alreadyCompleted }`. */
    CompleteJobStage = 'job.stage.complete',
    /** `POST /api/v2/public/stages` for a new stage on the run's Job, sent once per run and step (`Idempotency-Key`); `startDate` and `dueDate` are ISO date-times. Output `{ stageId }`. */
    CreateJobStage = 'job.stage.create',
    /** `PATCH /api/v2/public/jobs/:jobId` with the given fields only; setting a value is safe to repeat. Output `{ jobId }`. */
    UpdateJob = 'job.update',
}

/** One problem with a definition; `path` locates it (`steps[0].then[1].input.title`). */
export interface WorkflowValidationIssue {
    path: string;
    message: string;
}
